import { useState, useEffect, useRef } from 'react';
import DEFAULT_CONFIG from '../components/Data/podcast.json';
import { parseIdList, dedupePreserveOrder } from '../utils/expertUtils';

const qs = new URLSearchParams(window.location.search);

function getConfigUrl() {
  const fromQs = (qs.get('config') || '').trim();
  const fromEnv = (process.env.REACT_APP_WIDGET_CONFIG_URL || '').trim();
  if (fromQs) return fromQs;
  if (fromEnv) return fromEnv;
  return '';
}

/**
 * Hook to load and manage configuration
 * Handles: config loading, categories, VIP lists, globe icon
 */
export function useConfig() {
  const [config, setConfig] = useState(null);
  const [categories, setCategories] = useState([]);
  const [globeIconUrl, setGlobeIconUrl] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const vipAllRef = useRef([]);
  const vipByCategoryRef = useRef({});

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const cfgUrl = getConfigUrl();

      try {
        let cfg = null;

        if (cfgUrl) {
          const resp = await fetch(cfgUrl, { credentials: 'omit' });
          if (!resp.ok) throw new Error(`Config fetch failed (${resp.status})`);
          cfg = await resp.json();
        } else {
          cfg = DEFAULT_CONFIG;
        }

        if (cancelled) return;

        // Globe icon
        const globe = (cfg.globe_icon_url || '').trim()
          || 'https://qwoted.com/wp-content/uploads/2025/04/globe-1.svg';
        setGlobeIconUrl(globe);

        // Categories (ensure at least 'all')
        let cats = Array.isArray(cfg.categories) ? cfg.categories.slice() : [];
        cats = cats.map(c => ({
          ...c,
          isAllCategory: !!c.isAllCategory || c.key === 'all',
          icon: (c.icon || '').trim() || globe,
        }));

        if (!cats.length) {
          cats = [{
            key: 'all',
            label: 'All',
            desc: 'expert',
            icon: globe,
            isAllCategory: true,
          }];
        }
        setCategories(cats);

        // VIPs
        const vAll = dedupePreserveOrder(parseIdList(cfg.vip_all));
        vipAllRef.current = vAll;

        const vBy = cfg.vip_by_category && typeof cfg.vip_by_category === 'object'
          ? Object.fromEntries(
              Object.entries(cfg.vip_by_category).map(([k, v]) => [k, dedupePreserveOrder(parseIdList(v))])
            )
          : {};
        vipByCategoryRef.current = vBy;

        console.log('[Config] Loaded VIP counts by category:', Object.fromEntries(
          Object.entries(vBy).map(([k, v]) => [k, v.length])
        ));

        setConfig(cfg);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;

        setError(`Config error: ${String(err?.message || err)}`);
        setLoading(false);

        // Fallback to default 'all' category
        const defaultGlobe = 'https://qwotedstg.wpenginepowered.com/wp-content/uploads/2025/04/globe-1.svg';
        setGlobeIconUrl(defaultGlobe);
        setCategories([{
          key: 'all',
          label: 'All',
          desc: 'expert',
          icon: defaultGlobe,
          isAllCategory: true,
        }]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    config,
    categories,
    globeIconUrl,
    vipAllRef,
    vipByCategoryRef,
    loading,
    error,
  };
}
